import React, {useState, useEffect, useMemo} from "react";
import {useTable, usePagination, useSortBy, useExpanded, useResizeColumns, useFlexLayout} from "react-table";
import {QueryClient, QueryClientProvider, useQuery} from 'react-query'
import {IMPORT_CONFIGURATION_COLUMNS} from "./ImportConfigurationsColumns";
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination'
import {connect} from 'react-redux'
import {withRouter} from 'react-router-dom'
import UploadModal from './UploadModal'
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Input, Select, Button, Row as ARow, Col as ACol } from 'antd';

import {reducer, useEffectToDispatch, fetchApiData, initialState, Sorting} from "@/shared/apiTableHelper"
const { Option } = Select;

const queryClient = new QueryClient()

const DataTable = ({organization}) => {
  const [{queryPageIndex, queryPageSize, totalCount, queryPageFilter, queryPageSortBy}, dispatch] = React.useReducer(reducer, initialState);
  const [isOpenUploadModal, setOpenUploadModal] = useState(false);
  const [config, setConfig] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [filterTypeId, setFilterTypeId] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ name: '', typeId: '', source: '' });
  const [importTypes, setImportTypes] = useState([]);
  const [importSources, setImportSources] = useState([]);

  useEffect(() => {
    if (organization?.id) {
      axios.get(`/organization/${organization.id}/importtype`)
        .then(res => setImportTypes(res.data?.data || res.data || []))
        .catch(() => {});
      axios.get(`/organization/${organization.id}/importsource`)
        .then(res => setImportSources(res.data?.data || res.data || []))
        .catch(() => {});
    }
  }, [organization?.id]);

  const applyFilters = () => {
    setAppliedFilters({ name: filterName, typeId: filterTypeId, source: filterSource });
  };

  const resetFilters = () => {
    setFilterName(''); setFilterTypeId(''); setFilterSource('');
    setAppliedFilters({ name: '', typeId: '', source: '' });
  };

  const toggleUploadModal = () => {
    setOpenUploadModal(prev => !prev)
  }

  let import_list_columns = [
    ...IMPORT_CONFIGURATION_COLUMNS,
    {
      Header: "Action",
      accessor: "",
      Cell: ({ row }) => (
        <>
          <Link to={`/import/configurations/${row.original.id}`}>View</Link>
          &nbsp; | &nbsp;
          <Link to={`/import/configurations/${row.original.id}/edit`}>Edit</Link>
          &nbsp; | &nbsp;
          <Link to="#" onClick={() => handleImportClick(row)}>Import</Link>
          &nbsp; | &nbsp;
          <Link to={`/import/list?config=${row.original.id}`}>History</Link>
        </>
      ),
    },
  ]
  const handleImportClick = (row) => {
    setConfig(row.original);
    setOpenUploadModal(true);
  };
  let columns = useMemo(() => import_list_columns, [])

  const {isLoading, error, data, isSuccess} = useQuery(
    ['import', queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy, appliedFilters],
    () =>
    fetchApiData(
      {
        url: `/organization/${organization.id}/csv-import-setting`,
        page: queryPageIndex,
        size: queryPageSize,
        sortby: queryPageSortBy,
        filter: { name: appliedFilters.name, csv_import_type_id: appliedFilters.typeId }
      }
    ),
    {
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  const totalPageCount = Math.ceil(totalCount / queryPageSize)

  const filteredData = useMemo(() => {
    if (!data?.results) return [];
    if (!appliedFilters.source) return data.results;
    return data.results.filter(item => {
      const src = item.setups?.CsvImportSourceRequest?.id || '';
      return src.toLowerCase().includes(appliedFilters.source.toLowerCase());
    });
  }, [data, appliedFilters.source]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    state: {pageIndex, pageSize, sortBy}
  } = useTable({
      columns,
      data: filteredData,
      initialState: {
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
        sortBy: queryPageSortBy,
      },
      manualPagination: true, // Tell the usePagination
      pageCount: data ? totalPageCount : null,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
    },
    useSortBy,
    useExpanded,
    usePagination,
    useResizeColumns,
    useFlexLayout,
  );

  const manualPageSize = []

  useEffectToDispatch( dispatch, {pageIndex, pageSize, gotoPage, sortBy, data} );

  if (error) {
    return <p>Error: {JSON.stringify(error)}</p>;
  }

  if (isLoading) {
    return <p>Loading...</p>;
  }

  if (isSuccess)
    return (
      <>
        <ARow gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <ACol>
            <Input
              placeholder="Search by name"
              value={filterName}
              onChange={e => setFilterName(e.target.value)}
              onPressEnter={applyFilters}
              style={{ width: 220 }}
              allowClear
            />
          </ACol>
          <ACol>
            <Select
              placeholder="Import Type"
              value={filterTypeId || undefined}
              onChange={val => setFilterTypeId(val || '')}
              style={{ width: 200 }}
              allowClear
            >
              {importTypes.map(t => (
                <Option key={t.id} value={String(t.id)}>{t.name} ({t.context})</Option>
              ))}
            </Select>
          </ACol>
          <ACol>
            <Select
              placeholder="Import Source"
              value={filterSource || undefined}
              onChange={val => setFilterSource(val || '')}
              style={{ width: 200 }}
              allowClear
            >
              {importSources.map(s => (
                <Option key={s.id} value={s.display_name || String(s.id)}>{s.display_name || s.name}</Option>
              ))}
            </Select>
          </ACol>
          <ACol>
            <Button type="primary" onClick={applyFilters}>Search</Button>
          </ACol>
        </ARow>
        <div className='table react-table'>
          <table {...getTableProps()} className="table">
            <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    {column.isSorted ? <Sorting column={column}/> : ''}
                    <div
                      {...column.getResizerProps()}
                      className={`resizer ${
                        column.isResizing ? 'isResizing' : ''
                      }`}
                    />
                  </th>
                ))}
              </tr>
            ))}
            </thead>
            <tbody className="table table--bordered" {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row);
              // console.log(row)
              const subCount = (row.id.match(/\./g) || []).length
              // const paddingCount = subCount > 0 ? Number(subCount) + 3 : 0;
              // console.log(subCount)
              return (
                <tr {...row.getRowProps()}>
                  {
                    row.cells.map(cell => {
                      // console.log(cell)
                      const paddingLeft = subCount * 20
                      return <td {...cell.getCellProps()}><span
                        style={cell.column.Header === '#' ? {paddingLeft: `${paddingLeft}px`} : null}>{cell.render('Cell')}</span>
                      </td>
                    })
                  }
                </tr>
              )
            })}
            </tbody>
          </table>

        </div>
        {(rows.length > 0) && (
          <>
            <ReactTablePagination
              page={page}
              gotoPage={gotoPage}
              previousPage={previousPage}
              nextPage={nextPage}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageOptions={pageOptions}
              pageSize={pageSize}
              pageIndex={pageIndex}
              pageCount={pageCount}
              setPageSize={setPageSize}
              manualPageSize={manualPageSize}
              dataLength={totalCount}
            />
            <div className="pagination justify-content-end mt-2">
                            <span>
                            Go to page:{' '}
                              <input
                                type="number"
                                value={pageIndex + 1}
                                onChange={(e) => {
                                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                  gotoPage(page);
                                }}
                                style={{width: '100px'}}
                              />
                            </span>{' '}
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
              >
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {config && <UploadModal isOpen={isOpenUploadModal} setOpen={setOpenUploadModal} toggle={toggleUploadModal} config={config} />}
      </>
    )
}

const TableWrapper = ({organization}) => {
  if (!organization) return 'Loading...'
  return (
    <QueryClientProvider client={queryClient}>
      <DataTable organization={organization}/>
    </QueryClientProvider>
  )
}
export default withRouter(connect((state) => ({
  organization: state.organization
}))(TableWrapper));
